// src/components/admin/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth, useHasPermission } from '../../hooks/useAdminAuth';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredPermission?: 'read' | 'write' | 'delete' | 'manage_users';
    redirectTo?: string;
    allowedRoles?: string[]; // Nouveau : spécifier les rôles autorisés
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
                                                           children,
                                                           requiredPermission,
                                                           redirectTo = '/admin/login',
                                                           allowedRoles = ['admin', 'viewer'] // Par défaut, autoriser tous les rôles admin
                                                       }) => {
    const { user, loading } = useAdminAuth();
    const location = useLocation();
    const hasPermission = useHasPermission(requiredPermission || 'read');

    // Afficher le loader pendant la vérification
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <LoadingSpinner size="lg" />
                    <p className="mt-4 text-gray-600">Vérification des permissions...</p>
                </div>
            </div>
        );
    }

    // Rediriger vers la page de connexion si pas d'utilisateur
    if (!user) {
        return (
            <Navigate
                to={redirectTo}
                state={{ from: location }}
                replace
            />
        );
    }

    // Vérifier si le rôle de l'utilisateur est autorisé pour cette route
    const hasRoleAccess = allowedRoles.includes(user.role);

    if (!hasRoleAccess) {
        // Rediriger les viewers vers le dashboard s'ils tentent d'accéder à une page non autorisée
        if (user.role === 'viewer') {
            return <Navigate to="/admin/dashboard" replace />;
        }

        // Afficher un message d'erreur pour les autres cas
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">
                            Accès non autorisé
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Votre rôle ({user.role}) ne vous permet pas d'accéder à cette page.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => window.history.back()}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Retour
                            </button>
                            <Navigate to="/admin/dashboard" replace />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Vérifier les permissions spécifiques si requises
    if (requiredPermission && !hasPermission) {
        // Rediriger les viewers vers le dashboard au lieu d'afficher une erreur
        if (user.role === 'viewer') {
            return <Navigate to="/admin/dashboard" replace />;
        }

        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">
                            Permission insuffisante
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => window.history.back()}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Retour
                            </button>
                            <Navigate to="/admin/dashboard" replace />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Afficher le contenu protégé
    return <>{children}</>;
};

export default ProtectedRoute;
